import { createContext, ReactNode, useContext, useMemo } from 'react'
import { formatNumberWithoutCurrency, roundOffAndFormatNumberWithCurrency } from '../../utils/utils'

type AppMetaStatId = 'investors' | 'aumTracked' | 'eliteMembers'
type AppMetaStatFormat = 'long' | 'short' | 'shortUpper'

type AppMetaStat = {
  value: number
  prefix?: string
  suffix?: string
  showPlus?: boolean
}

type AppMetaContentContextValue = {
  getInvestorsValue: () => number
  getInvestorsDisplay: (format?: AppMetaStatFormat) => string
  getAumTrackedValue: () => number
  getAumTrackedDisplay: (format?: AppMetaStatFormat) => string
  getEliteMembersValue: () => number
  getEliteMembersDisplay: (format?: AppMetaStatFormat) => string
}

const appMetaStatsById: Record<AppMetaStatId, AppMetaStat> = {
  investors: {
    value: 1500000,
    showPlus: true
  },
  aumTracked: {
    value: 250000,
    prefix: '₹',
    suffix: ' Cr'
  },
  eliteMembers: {
    value: 110000,
    showPlus: true
  }
}

const AppMetaContentContext = createContext<AppMetaContentContextValue | undefined>(undefined)

export function AppMetaContentProvider({ children }: { children: ReactNode }) {
  const contextValue = useMemo<AppMetaContentContextValue>(
    () => ({
      getInvestorsValue: () => appMetaStatsById.investors?.value ?? 0,
      getInvestorsDisplay: (format = 'long') => {
        if (format === 'short') {
          return `${new Intl.NumberFormat('en', {
            notation: 'compact',
            compactDisplay: 'short',
            maximumFractionDigits: 1
          })
            .format(appMetaStatsById.investors?.value ?? 0)
            .toLowerCase()}+`
        }

        return `${formatNumberWithoutCurrency(appMetaStatsById.investors?.value ?? 0)}+`
      },
      getAumTrackedValue: () => appMetaStatsById.aumTracked?.value ?? 0,
      getAumTrackedDisplay: (format = 'long') => {
        if (format === 'short') {
          return `₹${new Intl.NumberFormat('en', {
            notation: 'compact',
            compactDisplay: 'short',
            maximumFractionDigits: 1
          })
            .format(appMetaStatsById.aumTracked?.value ?? 0)
            .toLowerCase()} Cr`
        }

        return `${roundOffAndFormatNumberWithCurrency(appMetaStatsById.aumTracked?.value ?? 0)} Cr`
      },
      getEliteMembersValue: () => appMetaStatsById.eliteMembers?.value ?? 0,
      getEliteMembersDisplay: (format = 'long') => {
        if (format === 'short' || format === 'shortUpper') {
          const compactValue = `${new Intl.NumberFormat('en', {
            notation: 'compact',
            compactDisplay: 'short',
            maximumFractionDigits: 1
          })
            .format(appMetaStatsById.eliteMembers?.value ?? 0)
            .toLowerCase()}+`

          if (format === 'shortUpper') {
            return compactValue.toUpperCase()
          }

          return compactValue
        }

        return `${formatNumberWithoutCurrency(appMetaStatsById.eliteMembers?.value ?? 0)}+`
      }
    }),
    []
  )

  return <AppMetaContentContext.Provider value={contextValue}>{children}</AppMetaContentContext.Provider>
}

export function useAppMetaContent() {
  const context = useContext(AppMetaContentContext)

  if (!context) {
    throw new Error('useAppMetaContent must be used within AppMetaContentProvider')
  }

  return context
}
